'use client';
import { useEffect, useRef } from 'react';

/**
 * Hero/CTA constellation · canvas con nodos animados.
 * Implementación intencionalmente liviana (no es una sim física):
 *   - N nodos con posición + drift suave
 *   - aristas a vecinos < threshold (Delaunay-light)
 *   - pulso de "señal" recorriendo aristas cada N segundos
 * `focus` (0..1) corre el centro de masa hacia el costado derecho del canvas
 *  para liberar la mitad izquierda al copy del hero.
 */
export function Constellation({ focus = 0.5 }: { focus?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    let w = 0, h = 0;
    const nodes: Array<{ x: number; y: number; vx: number; vy: number; r: number }> = [];
    const pulses: Array<{ from: number; to: number; t: number }> = [];

    function resize() {
      const rect = canvas!.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (nodes.length === 0) seed();
    }

    function seed() {
      const cx = w * focus;
      const cy = h * 0.5;
      const N = Math.max(28, Math.min(56, Math.floor((w * h) / 32000)));
      for (let i = 0; i < N; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.pow(Math.random(), 0.6) * Math.min(w, h) * 0.55;
        nodes.push({
          x: cx + Math.cos(angle) * dist,
          y: cy + Math.sin(angle) * dist * 0.85,
          vx: (Math.random() - 0.5) * 0.12,
          vy: (Math.random() - 0.5) * 0.12,
          r: 1.2 + Math.random() * 1.6,
        });
      }
    }

    function step() {
      ctx!.clearRect(0, 0, w, h);
      // edges
      const max = Math.min(w, h) * 0.16;
      ctx!.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < max) {
            const alpha = (1 - d / max) * 0.18;
            ctx!.strokeStyle = `rgba(122,160,204,${alpha.toFixed(3)})`;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.stroke();
          }
        }
      }
      // pulses
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        p.t += reduce ? 0 : 0.018;
        if (p.t >= 1) { pulses.splice(i, 1); continue; }
        const a = nodes[p.from], b = nodes[p.to];
        if (!a || !b) { pulses.splice(i, 1); continue; }
        const x = a.x + (b.x - a.x) * p.t;
        const y = a.y + (b.y - a.y) * p.t;
        ctx!.beginPath();
        ctx!.arc(x, y, 2.2, 0, Math.PI * 2);
        ctx!.fillStyle = 'rgba(166,198,232,0.95)';
        ctx!.shadowColor = 'rgba(122,160,204,0.8)';
        ctx!.shadowBlur = 10;
        ctx!.fill();
        ctx!.shadowBlur = 0;
      }
      // nodes
      for (const n of nodes) {
        if (!reduce) {
          n.x += n.vx;
          n.y += n.vy;
          if (n.x < 0 || n.x > w) n.vx *= -1;
          if (n.y < 0 || n.y > h) n.vy *= -1;
        }
        ctx!.beginPath();
        ctx!.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx!.fillStyle = 'rgba(244,243,238,0.65)';
        ctx!.fill();
      }
      raf = requestAnimationFrame(step);
    }

    function spawnPulse() {
      if (nodes.length < 2) return;
      const from = (Math.random() * nodes.length) | 0;
      let to = (Math.random() * nodes.length) | 0;
      if (to === from) to = (from + 1) % nodes.length;
      pulses.push({ from, to, t: 0 });
    }

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    step();
    const interval = reduce ? 0 : window.setInterval(spawnPulse, 900);

    return () => {
      cancelAnimationFrame(raf);
      if (interval) clearInterval(interval);
      ro.disconnect();
    };
  }, [focus]);

  return <canvas ref={ref} />;
}
